import i18next from 'i18next';
import { AppProps } from '../../../@types/AppProps';
import { Condition } from '../../../@types/Condition';
import { Valuation } from '../../../@types/Valuation';
import ButtonArrowRight from '../../../Components/Button/ButtonArrowRight';
import HtmlNode from '../../../Components/Extension/HtmlNode';
import InputField from '../../../Components/Input/InputField';
import InputRadioGroup from '../../../Components/Input/InputRadioGroup';
import getValuation, { getValuationErrorMessage } from '../../../Http/getValuation';
import { RequestError } from '../../../Http/http';
import Alert from '../../../Templates/Alert';
import { onlyNumbers } from '../../../Utils/format';
import RadioElements from './RadioElements';
import validation from './validation';

const INPUT_MILEAGE = 'input-milage';
const INPUT_MILEAGE_NODE = `${INPUT_MILEAGE}-node`;
const INPUT_CONDITION = 'input-condition';
const INPUT_CONDITION_NODE = `${INPUT_CONDITION}-node`;
const BUTTON_FETCH_VALUATION = 'button-fetch-valuation';

export interface MileageAndConditionState {
  registrationNumber: string;
  mileage: string;
  condition?: Condition;
}

interface MileageAndConditionStateLocal {
  value: {
    mileage: string;
    condition?: Condition;
  };
  validation: {
    mileage: boolean;
    condition: boolean;
  };
  interaction: {
    mileage: boolean;
    condition: boolean;
  };
}

type NameTypes = keyof MileageAndConditionStateLocal['value'];
type NameTypesString = Exclude<NameTypes, 'condition'>;
type NameTypesCondition = Exclude<NameTypes, 'mileage'>;

const createState = (_value: MileageAndConditionState): MileageAndConditionStateLocal => {
  const value = { ..._value };
  return {
    value,
    validation: {
      mileage: validation.mileage(value.mileage),
      condition: validation.condition(value.condition),
    },
    interaction: { mileage: false, condition: false },
  };
};

interface MileageAndConditionProps {
  state: MileageAndConditionState;
  settings: AppProps;
  onUpdate: (mileage: string, condition: Condition, valuation: Valuation) => void;
}

class MileageAndCondition extends HtmlNode {
  private readonly props: MileageAndConditionProps;
  private contexts: {
    mileage?: InputField;
    condition?: InputRadioGroup;
    buttonFetch?: ButtonArrowRight;
  } = {};
  private state: MileageAndConditionStateLocal;
  private requestError?: RequestError;
  private vehicle?: Valuation;

  constructor(element: HTMLElement | null, props: MileageAndConditionProps) {
    super(element);
    this.props = props;
    this.state = createState(this.props.state);
    this.render();
  }

  private onChange(e: Event) {
    const currentTarget = e.currentTarget as HTMLInputElement;
    const name = currentTarget.name as NameTypesString;
    const value = onlyNumbers(currentTarget.value);
    currentTarget.value = value;

    this.state.value[name] = value as string;
    this.state.validation[name] = validation[name](value);
    this.updateUiError(name);
    this.updateProceedButton();
  }

  private onBlur(e: Event) {
    const currentTarget = e.currentTarget as HTMLInputElement;
    const name = currentTarget.name as NameTypesString;

    this.state.interaction[name] = true;
    this.state.validation[name] = validation[name](this.state.value[name]);
    this.updateUiError(name);
    this.updateProceedButton();
  }

  private onKeyDown(e: KeyboardEvent) {
    if (e.key == 'Enter') {
      if (this.state.validation.mileage && this.state.validation.condition) {
        this.getVehicle();
      } else {
        this.onBlur(e);
        this.interactWithAll();
      }
    }
  }

  private onChangeRadio(e: Event) {
    const currentTarget = e.currentTarget as HTMLInputElement;
    const name = currentTarget.name as NameTypesCondition;
    const value = currentTarget.value as Condition;

    this.state.value[name] = value;
    this.state.validation[name] = validation[name](value);
    this.updateProceedButton();
  }

  private updateUiError(name: NameTypes) {
    this.contexts[name]?.setError(this.state.interaction[name] && !this.state.validation[name]);
  }

  private updateProceedButton() {
    this.contexts.buttonFetch?.disabled(!this.isValid());
  }

  private isValid() {
    return this.state.validation.mileage && this.state.validation.condition;
  }

  private interactWithAll() {
    Object.keys(this.state.interaction).forEach((key) => {
      this.state.interaction[key as NameTypes] = true;
      this.contexts[key as NameTypes]?.setError(
        this.state.interaction[key as NameTypes] && !this.state.validation[key as NameTypes]
      );
    });
  }

  async getVehicle() {
    if (!this.isValid()) {
      this.interactWithAll();
      return;
    }

    this.requestError = undefined;
    this.render();
    try {
      this.contexts.buttonFetch?.loading(true);
      const response = await getValuation(
        this.props.settings.url,
        this.props.state.registrationNumber,
        parseInt(this.state.value.mileage, 10),
        this.props.settings.marketCode,
        this.state.value.condition
      );
      this.vehicle = response;
    } catch (e) {
      this.requestError = e as RequestError;
    } finally {
      this.contexts.buttonFetch?.loading(false);
      this.render();
      if (this.state.value.condition && this.vehicle) {
        this.props.onUpdate(this.state.value.mileage, this.state.value.condition, this.vehicle);
      }
    }
  }

  render() {
    this.node.innerHTML = `
      <div class="waykevaluation-balloon waykevaluation-balloon--speak">
        <div class="waykevaluation-stack waykevaluation-stack--3">
          <h4 class="waykevaluation-heading waykevaluation-heading--3 waykevaluation-no-margin">
            ${i18next.t('mileageAndCondition.heading')}
          </h4>
        </div>
        <div class="waykevaluation-stack waykevaluation-stack--3" id="${INPUT_MILEAGE_NODE}"></div>
        <div class="waykevaluation-stack waykevaluation-stack--3" id="${INPUT_CONDITION_NODE}"></div>
        ${
          this.requestError
            ? `
              <div class="waykevaluation-stack waykevaluation-stack--3">
                ${Alert({
                  tone: 'error',
                  children: getValuationErrorMessage(this.requestError.status),
                })}
              </div>`
            : ''
        }
        <div class="waykevaluation-stack waykevaluation-stack--3" id="${BUTTON_FETCH_VALUATION}"></div>
      </div>
    `;

    this.contexts.mileage = new InputField(
      document.querySelector<HTMLDivElement>(`#${INPUT_MILEAGE_NODE}`),
      {
        title: i18next.t('input.mileage.label'),
        value: this.state.value.mileage,
        id: INPUT_MILEAGE,
        error: this.state.interaction.mileage && !this.state.validation.mileage,
        errorMessage: i18next.t('input.mileage.error') || undefined,
        name: 'mileage',
        autocomplete: 'off',
        placeholder: i18next.t('input.mileage.placeholder') || undefined,
        unit: i18next.t('glossary.mileageUnit_one') || undefined,
        pattern: '[0-9]',
        inputmode: 'numeric',
        onChange: (e) => this.onChange(e),
        onBlur: (e) => this.onBlur(e),
        onKeyDown: (e) => this.onKeyDown(e),
      }
    );

    this.contexts.condition = new InputRadioGroup(
      this.node.querySelector(`#${INPUT_CONDITION_NODE}`),
      {
        title: i18next.t('mileageAndCondition.conditionRadioGroup.label') || undefined,
        checked: this.state.value.condition as string,
        errorMessage: i18next.t('mileageAndCondition.conditionRadioGroup.error') || undefined,
        name: 'condition',
        options: RadioElements(),
        information: i18next.t('mileageAndCondition.conditionRadioGroup.help') || undefined,
        onClick: (e) => this.onChangeRadio(e),
      }
    );

    this.contexts.buttonFetch = new ButtonArrowRight(
      document.querySelector<HTMLDivElement>(`#${BUTTON_FETCH_VALUATION}`),
      {
        title: i18next.t('mileageAndCondition.seeEstimatedPurchasePrice'),
        disabled: !this.isValid(),
        onClick: () => this.getVehicle(),
      }
    );
  }
}

export default MileageAndCondition;
